/**
 * The app's own screen never zooms and never pans: a pinch, a double-tap or a rubber-band pull on the
 * chrome (the bars, the library, a sheet) moves nothing — only the plan, inside the editor, pans and
 * zooms, by its own gestures (editor/input.ts). The viewport is pinned at scale 1 (index.html; it also
 * keeps Safari from zooming onto a text field as it is focused), the page does not scroll or overscroll
 * (app.css), and here Safari's own pinch and double-tap zoom are refused everywhere, and a page nudged
 * out of place by the system is put back.
 */
export function lockViewport(): void {
  const refuse = (e: Event) => {
    if (e.cancelable) e.preventDefault();
  };
  // Safari's pinch-zoom of the page (and a two-finger touch that would begin one)
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(type, refuse, { passive: false });
  document.addEventListener('touchmove', (e) => (e.touches.length > 1 ? refuse(e) : undefined), { passive: false });
  // a trackpad pinch (a wheel with ctrl, in the browsers on a Mac) zooms only the plan, never the page
  document.addEventListener('wheel', (e) => (e.ctrlKey ? refuse(e) : undefined), { passive: false });
  // a double-tap never zooms the page (touch-action does it in CSS; this is for the browsers that ask)
  document.addEventListener('dblclick', refuse, { passive: false });
  // a page moved (the system scrolling it, say, when the keyboard goes) is put back where it belongs —
  // and while a field of the side panel is typed in, at once: the panel stands above the keyboard by
  // itself, so nothing need be scrolled into view (2026-10-28, Adam: "the entire viewport moves around")
  const inSide = () => !!document.activeElement?.closest('.side');
  const home = () => {
    const a = document.activeElement;
    const typing = a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement || (a instanceof HTMLElement && a.isContentEditable);
    if ((!typing || inSide()) && (window.scrollX || window.scrollY)) window.scrollTo(0, 0);
  };
  window.addEventListener('scroll', home, { passive: true });
  document.addEventListener('focusout', () => setTimeout(home, 50));
  const vv = window.visualViewport;
  const viewHome = () => {
    if (vv && inSide() && (vv.offsetTop || vv.offsetLeft)) window.scrollTo(0, 0);
  };
  vv?.addEventListener('scroll', viewHome);
  vv?.addEventListener('resize', viewHome);
  document.addEventListener('focusin', () => {
    viewHome();
    requestAnimationFrame(viewHome);
  });
  // the app's own layers never scroll (a field focused inside one would scroll it to show the field)
  document.addEventListener(
    'scroll',
    (e) => {
      const t = e.target;
      if (t instanceof HTMLElement && t.matches('#app, .screen, .stage, .side, .chrome') && (t.scrollTop || t.scrollLeft)) {
        t.scrollTop = 0;
        t.scrollLeft = 0;
      }
    },
    { capture: true, passive: true },
  );
}
